// Visual flowchart of an avatar's engine actions, n8n-style.
//
// - Step nodes left → right, edges = engine actions labeled by keyword category.
// - Edge target derived from action.emit:
//      empty/none      → next step  (blue solid)
//      goto-step-N     → step N     (green solid)
//      back-one-step   → prev step  (orange solid)
//      terminal emit   → End: <emit> node (red solid)
// - Default catch-all rows ("- no condition -") draw dashed.
// - Interrupt / Live overlays are dashed coloured edges, toggled by chips.
// - Read-only — clicking a step node closes the modal (so the user can edit
//   that step in the existing AvatarDetails UI).
import { useMemo, useState, useEffect } from 'react'
import { API_BASE_URL } from '../services/api'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath
} from 'reactflow'
import 'reactflow/dist/style.css'
import dagre from 'dagre'
import { X, Filter, Workflow } from 'lucide-react'

// ─── helpers ─────────────────────────────────────────────────────────────
const TERMINAL_EMITS = new Set([
  'transfer-call', 'do-not-call', 'not-interested', 'age-do-not-qualify',
  'answering-machine', 'busy', 'call-back',
  'engine-do-not-call-again', 'engine-not-interested'
])
const EMIT_COLOR = {
  default: '#60a5fa',     // blue
  goto:    '#22c55e',     // green
  back:    '#f59e0b',     // orange
  terminal:'#ef4444',     // red
  interrupt:'#a855f7',    // purple
  live:    '#ec4899',     // pink
  silence: '#94a3b8'      // slate
}
const TERMINAL_COLORS = {
  'transfer-call':       '#22c55e',
  'do-not-call':         '#ef4444',
  'not-interested':      '#94a3b8',
  'age-do-not-qualify':  '#f59e0b',
  'answering-machine':   '#a855f7',
  'busy':                '#64748b',
  'call-back':           '#06b6d4'
}

function isDefaultRule(a) {
  return !a.array || a.array === '- no condition -'
}
function parseEmit(rawEmit) {
  const emit = (rawEmit && rawEmit !== '- none -' && rawEmit !== '') ? rawEmit : null
  if (!emit) return { kind: 'next' }
  if (emit === 'back-one-step') return { kind: 'back' }
  const goto = /^goto-step-(\d+)$/.exec(emit)
  if (goto) return { kind: 'goto', step: parseInt(goto[1], 10) }
  if (TERMINAL_EMITS.has(emit)) return { kind: 'terminal', emit }
  return { kind: 'terminal', emit }  // unknown emit → terminal end node, by convention
}

// ─── custom nodes ────────────────────────────────────────────────────────
function StepNode({ data }) {
  const { step, actionCount, isFirst } = data
  return (
    <div style={{
      width: 200, padding: '14px 16px', borderRadius: 12,
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      border: isFirst ? '2px solid #22c55e' : '1px solid #334155',
      color: '#e2e8f0', boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
      position: 'relative'
    }}>
      <Handle type="target" position={Position.Left}  style={{ background: '#475569', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: '#475569', width: 10, height: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: isFirst ? '#22c55e' : '#3b82f6',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 14
        }}>{step}</div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Step {step}</div>
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>
        {actionCount} action{actionCount === 1 ? '' : 's'}{isFirst ? ' · entry' : ''}
      </div>
    </div>
  )
}

function TerminalNode({ data }) {
  const { emit } = data
  const color = TERMINAL_COLORS[emit] || '#ef4444'
  return (
    <div style={{
      minWidth: 180, padding: '12px 14px', borderRadius: 12,
      background: '#1e293b',
      border: `2px solid ${color}`,
      color: '#e2e8f0', boxShadow: '0 4px 14px rgba(0,0,0,0.4)'
    }}>
      <Handle type="target" position={Position.Left} style={{ background: color, width: 10, height: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color }} />
        <div style={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>End</div>
      </div>
      <div style={{ fontSize: 13, color: '#cbd5e1', fontFamily: 'monospace' }}>{emit}</div>
    </div>
  )
}

const NODE_TYPES = { step: StepNode, terminal: TerminalNode }

// Edge that shows the condition as a small label and the audio/voice as a round
// badge ("circle") sitting on the line, with a ♪ and the audio name inside it.
function AudioEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, style, data }) {
  const [path, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition })
  const color = (style && style.stroke) || '#3b82f6'
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div style={{
          position: 'absolute', transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
          display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none', whiteSpace: 'nowrap'
        }}>
          {data?.cond && (
            <span style={{ fontSize: 10, fontWeight: 500, color: '#cbd5e1', background: '#0f172a', padding: '2px 5px', borderRadius: 4, opacity: 0.9 }}>{data.cond}</span>
          )}
          {data?.audio && (
            <span title={data.audioFull || data.audio} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              minWidth: 22, height: 22, padding: '0 9px', borderRadius: 999,
              background: color, color: '#fff', fontSize: 10, fontWeight: 600,
              border: '2px solid #0f172a', boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
            }}>♪ {data.audio}</span>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
const EDGE_TYPES = { audio: AudioEdge }

// ─── layout via dagre (left → right) ─────────────────────────────────────
function layout(nodes, edges) {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 70, ranksep: 170, edgesep: 35, marginx: 40, marginy: 40, ranker: 'network-simplex' })
  nodes.forEach(n => g.setNode(n.id, { width: 200, height: 80 }))
  edges.forEach(e => g.setEdge(e.source, e.target))
  dagre.layout(g)
  return nodes.map(n => {
    const p = g.node(n.id)
    return { ...n, position: { x: p.x - 100, y: p.y - 40 } }
  })
}

// ─── build nodes + edges from engineActions ──────────────────────────────
function buildGraph(engineActions, filters) {
  const actionsByType = {
    regular:   engineActions.filter(a => (a.action_type || 'regular') === 'regular'),
    interrupt: engineActions.filter(a => a.action_type === 'interrupt'),
    live:      engineActions.filter(a => a.action_type === 'live')
  }
  const visible = []
  if (filters.regular)   visible.push(...actionsByType.regular.map(a => ({ ...a, _kind: 'regular'   })))
  if (filters.interrupt) visible.push(...actionsByType.interrupt.map(a => ({ ...a, _kind: 'interrupt' })))
  if (filters.live)      visible.push(...actionsByType.live.map(a => ({ ...a, _kind: 'live'      })))

  const stepNumbers = new Set([1])
  engineActions.forEach(a => { const sn = +(a.step_number ?? a.step); if (Number.isFinite(sn)) stepNumbers.add(sn) })
  visible.forEach(a => {
    const t = parseEmit(a.emit)
    if (t.kind === 'goto') stepNumbers.add(t.step)
  })
  const maxStep = Math.max(...stepNumbers, 1)
  for (let i = 1; i <= maxStep; i++) stepNumbers.add(i)
  const steps = [...stepNumbers].sort((a, b) => a - b)

  const stepActionCount = {}
  actionsByType.regular.forEach(a => {
    const s = +(a.step_number ?? a.step) || 1
    stepActionCount[s] = (stepActionCount[s] || 0) + 1
  })

  const nodes = []
  steps.forEach(s => {
    nodes.push({
      id: `step-${s}`, type: 'step',
      data: { step: s, actionCount: stepActionCount[s] || 0, isFirst: s === 1 },
      position: { x: 0, y: 0 }
    })
  })

  // Collapse the many rules that share the same (from-step → outcome) into ONE
  // edge with a combined label, so the graph is readable instead of a hairball.
  const termNodes = new Map()   // per-step terminal nodes: id -> emit
  const edgeMap = new Map()
  visible.forEach((a) => {
    const fromStep = +(a.step_number ?? a.step) || 1
    const target = parseEmit(a.emit)
    const isDefault = isDefaultRule(a)
    if (isDefault && !filters.defaults) return
    // "Step links only" — hide terminal (END) branches, keep step→step flow.
    if (filters.stepLinks && target.kind === 'terminal') return

    let targetNodeId, edgeKind  // 'default'|'goto'|'back'|'terminal'
    if (target.kind === 'terminal') {
      // local to this step → short edge, no long cross-graph line to a shared END
      targetNodeId = `end-${fromStep}-${target.emit}`; termNodes.set(targetNodeId, target.emit); edgeKind = 'terminal'
    } else if (target.kind === 'goto') {
      targetNodeId = `step-${target.step}`; edgeKind = 'goto'
    } else if (target.kind === 'back') {
      targetNodeId = `step-${Math.max(1, fromStep - 1)}`; edgeKind = 'back'
    } else {
      targetNodeId = `step-${fromStep + 1 <= maxStep ? fromStep + 1 : maxStep}`; edgeKind = 'default'
    }

    const baseColor = a._kind === 'interrupt' ? EMIT_COLOR.interrupt
                    : a._kind === 'live'      ? EMIT_COLOR.live
                    : EMIT_COLOR[edgeKind]
    const key = `step-${fromStep}|${targetNodeId}|${a._kind}`
    const cond = isDefault ? '(default)' : (a.array || '?')
    let e = edgeMap.get(key)
    if (!e) {
      e = { source: `step-${fromStep}`, target: targetNodeId, kind: a._kind, edgeKind, isDefault, baseColor, conds: [], audios: new Set() }
      edgeMap.set(key, e)
    }
    if (!e.conds.includes(cond)) e.conds.push(cond)
    // the audio/voice this action plays on the way out
    ;[a.audio_category_1, a.audio_category_2, a.audio_category_3].forEach(ac => { if (ac && ac !== '- none -') e.audios.add(ac) })
    if (!isDefault) e.isDefault = false   // a real condition makes the merged edge "solid"
  })

  const edges = [...edgeMap.values()].map((e, idx) => {
    const prefix = e.kind === 'regular' ? '' : (e.kind.toUpperCase() + ' · ')
    const condPart = e.conds.length <= 2 ? e.conds.join(', ') : `${e.conds.slice(0, 2).join(', ')} +${e.conds.length - 2}`
    const auds = [...e.audios]
    const audio = auds.length ? (auds.length <= 2 ? auds.join(' + ') : `${auds.slice(0, 2).join(' + ')} +${auds.length - 2}`) : ''
    return {
      id: `e-${idx}-${e.source}-${e.target}`,
      source: e.source, target: e.target,
      type: 'audio',
      animated: false,
      data: { cond: prefix + condPart, audio, audioFull: auds.join(', ') },
      style: {
        stroke: e.baseColor,
        strokeWidth: 2,
        strokeDasharray: (e.kind !== 'regular' || e.isDefault) ? '5 4' : undefined,
        opacity: e.isDefault ? 0.5 : 0.95
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: e.baseColor }
    }
  })

  termNodes.forEach((emit, id) => {
    nodes.push({
      id, type: 'terminal',
      data: { emit }, position: { x: 0, y: 0 }
    })
  })

  return { nodes: layout(nodes, edges), edges }
}

// ─── modal shell ─────────────────────────────────────────────────────────
export default function AvatarMap({ avatarName, avatarId, engineActions, onClose }) {
  const [filters, setFilters] = useState({ regular: true, interrupt: false, live: false, defaults: true, stepLinks: false })
  // Fetch engine actions directly if the parent didn't pass any (e.g. map opened
  // from a tab other than Engine, so the parent's engineActions are still empty).
  const [fetched, setFetched] = useState(null)
  useEffect(() => {
    if ((engineActions && engineActions.length) || !avatarId) return
    let cancelled = false
    fetch(`${API_BASE_URL}/tables/engine_actions?field=avatar_id&val=${avatarId}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setFetched(Array.isArray(d) ? d : (d.data || [])) })
      .catch(() => { if (!cancelled) setFetched([]) })
    return () => { cancelled = true }
  }, [avatarId, engineActions])
  const actions = (engineActions && engineActions.length) ? engineActions : (fetched || [])
  const { nodes, edges } = useMemo(() => buildGraph(actions, filters), [actions, filters])
  // Mirror the computed graph into React Flow state so nodes are draggable and
  // keep their new position (re-layout only when the graph itself changes).
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([])
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([])
  useEffect(() => { setRfNodes(nodes); setRfEdges(edges) }, [nodes, edges, setRfNodes, setRfEdges])

  const toggle = (k) => setFilters(f => ({ ...f, [k]: !f[k] }))
  const chip = (k, label, color) => (
    <button
      key={k}
      onClick={() => toggle(k)}
      style={{
        background: filters[k] ? color : 'transparent',
        color: filters[k] ? '#fff' : '#94a3b8',
        border: `1px solid ${color}`,
        padding: '4px 10px', borderRadius: 12, fontSize: 12, cursor: 'pointer'
      }}
    >{label}</button>
  )

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#020617', zIndex: 1000,
        display: 'flex', flexDirection: 'column'
      }}
    >
      <div style={{
        padding: '12px 18px', borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#0f172a'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Workflow size={18} color="#60a5fa" />
          <div>
            <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 15 }}>Avatar map</div>
            <div style={{ color: '#94a3b8', fontSize: 12 }}>{avatarName || ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Filter size={14} color="#94a3b8" />
          <span style={{ color: '#94a3b8', fontSize: 12, marginRight: 4 }}>show:</span>
          {chip('regular',   'Regular',     EMIT_COLOR.default)}
          {chip('interrupt', 'Interrupts',  EMIT_COLOR.interrupt)}
          {chip('live',      'Live',        EMIT_COLOR.live)}
          {chip('defaults',  'Defaults',    '#475569')}
          {chip('stepLinks', 'Step links only', '#0ea5e9')}
          <button
            onClick={onClose}
            style={{
              marginLeft: 12, background: 'transparent', border: '1px solid #334155',
              color: '#e2e8f0', borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4
            }}
          ><X size={14} /> Close</button>
        </div>
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          nodesDraggable
          fitView
          minZoom={0.2}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1e293b" gap={20} size={1} />
          <Controls style={{ background: '#1e293b', border: '1px solid #334155' }} />
          <MiniMap
            nodeColor={(n) => n.type === 'terminal' ? (TERMINAL_COLORS[n.data?.emit] || '#ef4444') : (n.data?.isFirst ? '#22c55e' : '#3b82f6')}
            maskColor="rgba(2,6,23,0.7)"
            style={{ background: '#0f172a', border: '1px solid #334155' }}
          />
        </ReactFlow>

        {nodes.length === 1 && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            color: '#64748b', fontSize: 14, textAlign: 'center'
          }}>
            No engine actions yet for this avatar. Add some on the Engine Actions tab.
          </div>
        )}
      </div>
    </div>
  )
}
